import React, { useEffect, useMemo, useState } from 'react';

import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import cx from 'classnames';
import { ASSIGNMENT_CREATE_MODES } from 'constants/assignments';
import { isNull } from 'lodash';
import {
  createTrackerFromAssignmentTemplate,
  mathAssignmentCreateViaCopy,
  useAssignmentTemplate,
  useTrackerDetail
} from 'sdk';
import { useUserSubjectSections } from 'sdk';

import { ReactComponent as ArrownDownIcon } from 'pages/Teachers/Assignments/CreateMathV2/assets/arrow-down-icon.svg';
import { ReactComponent as DocumentIcon } from 'pages/Teachers/Assignments/CreateMathV2/assets/document-icon.svg';
import { ReactComponent as SubjectIcon } from 'pages/Teachers/Assignments/CreateMathV2/assets/subject-icon.svg';
import ClassSelector from 'pages/Teachers/Assignments/CreateMathV2/components/ClassSelector';
import ExemplarUpload from 'pages/Teachers/Assignments/CreateMathV2/components/ExemplarUpload';
import RubricSelector from 'pages/Teachers/Assignments/CreateMathV2/components/RubricSelector';
import StandardSelector from 'pages/Teachers/Assignments/CreateMathV2/components/StandardSelector';
import TemplateDropdown from 'pages/Teachers/Assignments/CreateMathV2/components/TemplateDropdown';
import SwitchToggle from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/SwitchToggle';
import { assignmentAssign } from 'pages/Teachers/shared/AssignmentDialog/components/AssignAssignment/sdk';
import { VALUES_OPTIONS } from 'pages/Teachers/shared/AssignmentDialog/constants';
import { trackerCreate } from 'pages/Teachers/shared/AssignmentDialog/sdk';
import {
  buildAssignmentFormData,
  validateAssignmentForm
} from 'utils/assignments';
import { useEdLightTracking } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyError, notifyErrors } from 'utils/notifications';
import { processSections } from 'utils/sections';

import styles from './styles.module.scss';

const CreateMathForm = ({ mode, setMode, onCreationSuccess }) => {
  const [assignmentName, setAssignmentName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTracker, setSelectedTracker] = useState(null);
  const [shareAcrossDistricts, setShareAcrossDistricts] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const [maxGradeValue, setMaxGradeValue] = useState(100);

  const { data: rawSectionsResponse, isLoading: isLoadingSections } =
    useUserSubjectSections({ subject_name: selectedSubject });

  const { trackEvent } = useEdLightTracking({
    feature: 'create',
    view: 'type'
  });

  const templateResult = useAssignmentTemplate({
    assignmentTemplateId: selectedTemplate?.assignment_template_id || null,
    params: { is_accelerated: selectedTemplate?.is_accelerated }
  });

  const trackerResult = useTrackerDetail({
    trackerId: selectedTracker?.tracker_id || null
  });

  useEffect(() => {
    if (selectedTracker) {
      setMode(ASSIGNMENT_CREATE_MODES.COPY);
    }
  }, [selectedTracker, setMode]);

  const isFormValid = validateAssignmentForm({
    mode,
    assignmentName,
    selectedSections,
    selectedRubric,
    selectedStandards,
    uploadedFile
  });

  const sections = useMemo(() => {
    const actualData = Array.isArray(rawSectionsResponse)
      ? rawSectionsResponse
      : rawSectionsResponse?.data || [];

    return processSections({ data: actualData });
  }, [rawSectionsResponse]);

  const handleCreateAssignment = async () => {
    setIsSubmitting(true);

    try {
      const formData = buildAssignmentFormData({
        assignmentName,
        description,
        uploadedFile,
        selectedRubric,
        selectedStandards,
        maxGradeValue,
        shareAcrossDistricts,
        isAccelerated: selectedTemplate?.is_accelerated || false,
        mode
      });

      trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
        view: 'create',
        form: 'create',
        pages: uploadedFile?.pages || 1,
        rubric: selectedRubric?.key || 'none',
        subject: selectedSubject,
        has_qa: false
      });

      let createSuccess, createData, createErrors;

      // Call the appropriate API based on the mode
      if (mode === ASSIGNMENT_CREATE_MODES.TEMPLATE) {
        const assignmentTemplateId = selectedTemplate?.assignment_template_id;
        const trackerId = selectedTracker?.tracker_id;

        if (!assignmentTemplateId && !trackerId) {
          notifyError('Please select a template before creating.');
          return;
        }

        const response = await createTrackerFromAssignmentTemplate({
          assignmentTemplateId,
          data: formData
        });

        createSuccess = response.success;
        createData = response.data;
        createErrors = response.errors;
      } else if (mode === ASSIGNMENT_CREATE_MODES.SCRATCH) {
        const response = await trackerCreate(formData);

        createSuccess = response.success;
        createData = response.data;
        createErrors = response.errors;
      } else if (mode === ASSIGNMENT_CREATE_MODES.COPY) {
        const trackerId = selectedTracker?.tracker_id;

        if (!trackerId) {
          notifyError('Please select a template before creating.');
          return;
        }

        const response = await mathAssignmentCreateViaCopy({
          trackerId,
          data: formData
        });

        createSuccess = response.success;
        createData = response.data;
        createErrors = response.errors;
      }

      if (!createSuccess) {
        notifyErrors(createErrors);
        return;
      }

      const defaultOpen = new Date();
      const defaultDue = new Date();
      defaultDue.setDate(defaultOpen.getDate() + 2);

      const assignmentPayload = {
        sections_data: selectedSections.map((section) => ({
          section_id: section.id,
          open: section.open
            ? new Date(section.open).toISOString()
            : defaultOpen.toISOString(),
          due: section.due
            ? new Date(section.due).toISOString()
            : defaultDue.toISOString()
        }))
      };

      trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
        view: 'assign',
        form: 'assign',
        classes: selectedSections?.length || 0
      });

      const { success: assignSuccess, errors: assignErrors } =
        await assignmentAssign(createData.id, assignmentPayload);

      if (!assignSuccess) {
        notifyErrors(assignErrors);
        return;
      }

      localStorage.setItem(
        'pending_created_assignment',
        JSON.stringify({
          tracker_id: createData.id,
          name: assignmentName
        })
      );

      onCreationSuccess({ createdTrackerId: createData.id });
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    const isSwitchingToScratch = mode === ASSIGNMENT_CREATE_MODES.TEMPLATE;

    setMode((prev) =>
      prev === ASSIGNMENT_CREATE_MODES.TEMPLATE
        ? ASSIGNMENT_CREATE_MODES.SCRATCH
        : ASSIGNMENT_CREATE_MODES.TEMPLATE
    );

    trackEvent(`${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`, {
      button: isSwitchingToScratch ? 'create' : 'template'
    });

    setSelectedTemplate(null);
    setSelectedTracker(null);
    setUploadedFile(null);
  };

  const handleTemplateSelection = (template) => {
    if (!template) {
      setSelectedTemplate(null);
      setSelectedTracker(null);
      return;
    }
    if (template.assignment_template_id) {
      setSelectedTemplate(template);
    }

    if (template.tracker_id) {
      setSelectedTracker(template);
    }

    if (template) {
      setAssignmentName(template.assignment_template_title);

      trackEvent(`${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`, {
        button: 'copy'
      });
    }
  };

  useEffect(() => {
    const data = templateResult?.data;

    if (!data) return;

    if (data.title) {
      setAssignmentName(data.title);
    }

    if (data.description) {
      setDescription(data.description);
    }

    let pagesCount = 1;

    if (data.tasks && data.tasks.length > 0) {
      const firstTask = data.tasks[0];
      pagesCount = data.tasks.length;

      if (firstTask.standards) {
        setSelectedStandards(firstTask.standards);
      }

      if (firstTask.value_type) {
        const presetOption = VALUES_OPTIONS.find(
          (opt) => opt.key === firstTask.value_type
        );

        if (presetOption) {
          setSelectedRubric(presetOption);
        }
      }

      if (firstTask.max_value !== undefined) {
        setMaxGradeValue(firstTask.max_value);
      }
    }

    if (data.attachments && data.attachments.length > 0) {
      const attachment = data.attachments[0];

      setUploadedFile({
        id: attachment.file.id,
        name: attachment.file.name,
        url: attachment.file.url,
        type: attachment.file.type,
        pages: pagesCount
      });
    } else {
      setUploadedFile({ pages: pagesCount });
    }
  }, [templateResult?.data]);

  useEffect(() => {
    const data = trackerResult?.data;

    if (!data) return;

    if (data.name) {
      setAssignmentName(data.name);
    }

    if (data.description) {
      setDescription(data.description);
    }

    let pagesCount = 1;

    if (data.tasks && data.tasks.length > 0) {
      const firstTask = data.tasks[0];
      pagesCount = data.tasks.length;

      if (firstTask.standards) {
        setSelectedStandards(firstTask.standards);
      }

      if (firstTask.value_type) {
        const presetOption = VALUES_OPTIONS.find(
          (opt) => opt.key === firstTask.value_type
        );

        if (presetOption) {
          setSelectedRubric(presetOption);
        }
      }

      if (firstTask.max_value !== undefined) {
        setMaxGradeValue(firstTask.max_value);
      }
    }

    if (data.attachments && data.attachments.length > 0) {
      const attachment = data.attachments[0];

      setUploadedFile({
        id: attachment.file.id,
        name: attachment.file.name,
        url: attachment.file.url,
        type: attachment.file.type,
        pages: pagesCount
      });
    } else {
      setUploadedFile({ pages: pagesCount });
    }
  }, [trackerResult?.data]);

  const handleSelectedSectionsChange = (newSelectedSections) => {
    setSelectedSubject(newSelectedSections);
    setAssignmentName('');
    setDescription('');
    setSelectedTemplate(null);
  };

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.container}>
        {/* --- HEADER --- */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>Launch New Assignment</h3>
          </div>
          <div className={styles.fieldSubject}>
            <div className={styles.fieldLabel}>
              <SubjectIcon /> <span>Subject</span>
            </div>
            <div className={styles.selectWrapper}>
              <select
                defaultValue=""
                onChange={(e) => handleSelectedSectionsChange(e.target.value)}
                className={styles.inputField}
              >
                <option value="" disabled>
                  Select subject...
                </option>
                <option key="math" value="math">
                  Math
                </option>
                <option key="ela" value="ELA">
                  ELA
                </option>
              </select>
              <ArrownDownIcon className={styles.chevron} />
            </div>
          </div>
        </div>
        {!isNull(selectedSubject) && (
          <>
            <div className={styles.assignmentTypeRow}>
              <div className={styles.typeLabel}>
                <DocumentIcon />
                <span>Assignment</span>
              </div>
              <button onClick={toggleMode} className={styles.modeToggle}>
                {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE
                  ? 'Create from scratch →'
                  : 'Use a Template →'}
              </button>
            </div>

            {/* --- FORM CONTENT --- */}

            <div className={styles.formContent}>
              {/* Template Search (Template Mode Only) */}
              {(mode === ASSIGNMENT_CREATE_MODES.TEMPLATE ||
                mode === ASSIGNMENT_CREATE_MODES.COPY) && (
                <TemplateDropdown
                  selectedTemplate={selectedTemplate || selectedTracker}
                  onSelectTemplate={handleTemplateSelection}
                  trackEvent={trackEvent}
                  subject={selectedSubject}
                />
              )}

              <input
                type="text"
                className={styles.inputField}
                placeholder="Assignment Name"
                value={assignmentName}
                onChange={(e) => setAssignmentName(e.target.value)}
              />

              <textarea
                className={cx(styles.inputField, styles.textArea)}
                placeholder="Add a Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />

              {/* Learning Standard (Scratch Mode Only) */}
              {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
                <StandardSelector
                  selectedStandards={selectedStandards}
                  setSelectedStandards={setSelectedStandards}
                  subject={selectedSubject}
                />
              )}

              <ClassSelector
                sections={sections}
                selectedSections={selectedSections}
                setSelectedSections={setSelectedSections}
                isLoading={isLoadingSections}
              />

              <RubricSelector
                options={VALUES_OPTIONS}
                selected={selectedRubric}
                onSelect={setSelectedRubric}
                maxGrade={maxGradeValue}
                onMaxGradeChange={(newValue) => setMaxGradeValue(newValue)}
              />

              {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
                <>
                  <ExemplarUpload
                    file={uploadedFile}
                    setFile={setUploadedFile}
                  />
                  {selectedSubject === 'math' && (
                    <SwitchToggle
                      checked={shareAcrossDistricts}
                      onChange={(e) =>
                        setShareAcrossDistricts(e.target.checked)
                      }
                    />
                  )}
                </>
              )}
            </div>

            {/* --- FOOTER --- */}
            <div className={styles.footer}>
              <button
                className={styles.createButton}
                disabled={!isFormValid || isSubmitting}
                onClick={handleCreateAssignment}
              >
                {isSubmitting ? (
                  'Processing...'
                ) : (
                  <>
                    <span></span>
                    CREATE ASSIGNMENT
                    <ArrowForwardIcon />
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateMathForm;
